import { type FC, useState } from 'react'

import {
  contentSection,
  menuCoverImage,
  mutedText,
  offlineText,
} from '@liveHelper/components/Idle/Idle.twind'
import { expandedHeaderImageContainer } from '@liveHelper/components/shared/shared.twind'
import {
  CTACloseButton,
  FireworkLogoWrapper,
  IdleExpandedContainer,
} from '@liveHelper/components/shared/styles'
import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import {
  useFormatOfficeHours,
  useNextAvailableOfficeHour,
} from '@liveHelper/hooks/useOfficeHoursTimeSlots'
import { useStartScheduling } from '@liveHelper/hooks/useStartScheduling'
import {
  actionsSelector,
  paramsSelector,
  useStore,
} from '@liveHelper/models/store'
import { LIVE_HELPER_WIDGET_BASE_CONTAINER } from '@liveHelper/testIds'
import { useGetUtmContent } from '@liveHelper/utils/useGetUtmContent'
import { ChevronDown } from '@oneToOne/shared/assets/ChevronDown'
import { Close } from '@oneToOne/shared/assets/Close'
import { ScheduleSVG } from '@oneToOne/shared/assets/ScheduleSVG'
import { FireworkBranding } from '@oneToOne/shared/components/FireworkBranding'

import { Button } from '@core/components/twind/Button'
import { useContextTranslation } from '@core/i18n'
import { useTwindContext } from '@core/twind'
import { isMobileWeb } from '@core/userAgent'

const OfficeHourDaySchedule: FC<{
  officeHour?: Record<string, string>
  showBorder?: boolean
}> = ({ officeHour, showBorder = false }) => {
  const { tw } = useTwindContext()

  // { Thursday: '9:00 AM - 12:00 PM\n2:00 PM - 6:00 PM' }
  if (!officeHour || Object.keys(officeHour).length === 0) {
    return null
  }

  const [key, value] = Object.entries(officeHour)[0]
  return (
    <div
      className={tw?.(
        'flex',
        'justify-between',
        'items-center',
        'text-right',
        'w-full',
        'py-2.5',
        showBorder ? 'border-b' : '',
      )}
    >
      <span className={tw?.(offlineText, 'text-sm')}>{key}</span>
      <span className={tw?.(offlineText, 'text-sm', 'whitespace-pre-wrap')}>
        {value}
      </span>
    </div>
  )
}

/**
 * Idle expanded state with offline office hour + Multiple time slots
 */
export const IdleExpandedOfflineTimeSlots: FC = () => {
  const { t } = useContextTranslation()
  const { tw } = useTwindContext()

  const params = useStore(paramsSelector)
  const { sendUIStateTransitionEvent } = useStore(actionsSelector)

  const [showOfficeHours, setShowOfficeHours] = useState<boolean>(false)

  const chatChannelConfig = useChatChannelConfig()
  const utmContent = useGetUtmContent()
  const startScheduling = useStartScheduling()
  const nextAvailableOfficeHour = useNextAvailableOfficeHour()
  const isNextOfficeHourAvailable = Boolean(
    nextAvailableOfficeHour &&
    chatChannelConfig?.widget_style?.show_offline_office_hours,
  )
  const isSchedulingEnabled = Boolean(
    chatChannelConfig?.config?.scheduling_provider !==
      'SCHEDULING_PROVIDER_NONE' &&
    chatChannelConfig?.widget_style?.show_offline_scheduling,
  )

  const officeHours = useFormatOfficeHours()

  return (
    <IdleExpandedContainer
      data-testid={LIVE_HELPER_WIDGET_BASE_CONTAINER}
      isMobileWebEnabled={isMobileWeb()}
    >
      <CTACloseButton
        shape="circle"
        aria-label={t(`oneToOne:End Conversation`)}
        background="var(--gray-9)"
        buttonSize="sm"
        onClick={(e) => {
          e.stopPropagation()
          sendUIStateTransitionEvent('MINIMIZE')
        }}
        icon={<Close width="16px" fill="var(--white)" label={'close'} />}
      />
      {!showOfficeHours ? (
        <div className={tw?.(expandedHeaderImageContainer, 'overflow-hidden')}>
          <div
            className={tw?.(
              menuCoverImage(
                chatChannelConfig?.widget_style?.menu_cover_image_url,
              ),
            )}
          ></div>
        </div>
      ) : null}
      <div className={tw?.(contentSection, 'px-10', 'pt-10', 'pb-7')}>
        <div
          className={tw?.('flex', 'flex-col', 'justify-center', 'items-center')}
        >
          <div className={tw?.('flex', 'flex-col', 'text-center')}>
            <span className={tw?.(offlineText, 'text-lg', 'font-semibold')}>
              {t('oneToOne:We are currently unavailable')}
            </span>
            <span className={tw?.(mutedText, 'text-sm')}>
              {isSchedulingEnabled
                ? t(`oneToOne:Let's talk. Schedule a call with us.`)
                : t('oneToOne:Our human agents are currently unavailable.')}
            </span>
            {isNextOfficeHourAvailable ? (
              <span className={tw?.(mutedText, 'text-sm')}>
                {t('See our working hours below.')}
              </span>
            ) : null}
          </div>

          {isNextOfficeHourAvailable ? (
            !showOfficeHours ? (
              <div
                className={tw?.(
                  'flex',
                  'items-center',
                  'gap-2',
                  'px-2.5',
                  'w-full',
                )}
                onClick={() => setShowOfficeHours(!showOfficeHours)}
                aria-hidden={true}
              >
                <OfficeHourDaySchedule officeHour={nextAvailableOfficeHour} />
                <ChevronDown
                  width="18px"
                  height="18px"
                  fill="#595959"
                  label={'Expand'}
                />
              </div>
            ) : (
              <div
                className={tw?.(
                  'flex',
                  'flex-col',
                  'my-2.5',
                  'px-2.5',
                  'w-full',
                  'max-h-96',
                  'overflow-y-auto',
                )}
              >
                {officeHours.map((o, i) => (
                  <OfficeHourDaySchedule
                    key={i}
                    officeHour={o}
                    showBorder={i !== officeHours.length - 1}
                  />
                ))}
              </div>
            )
          ) : null}

          {!isSchedulingEnabled ? <div style={{ height: '20px' }}></div> : null}
          {isSchedulingEnabled ? (
            <Button
              className={tw?.('mt-4')}
              aria-label={t(`oneToOne:Book now`)}
              onClick={startScheduling}
              block
            >
              <div className={tw?.('flex', 'gap-2', 'items-center')}>
                <ScheduleSVG
                  height="14px"
                  width="14px"
                  fill="var(--white)"
                  label={'schedule'}
                />
                {t(`oneToOne:Schedule`)}
              </div>
            </Button>
          ) : null}
        </div>
      </div>
      {chatChannelConfig?.widget_style?.firework_branding ? (
        <FireworkLogoWrapper>
          <FireworkBranding
            dark={true}
            utmContent={utmContent}
            lang={params.lang}
          />
        </FireworkLogoWrapper>
      ) : null}
    </IdleExpandedContainer>
  )
}
